import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Paper,
  Stack,
  Chip,
} from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import { useTranslation } from '../hooks/useTranslation';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { useLanguage } from '../contexts/LanguageContext';

const StyledPaper = styled(Paper)(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(4),
  borderRadius: theme.spacing(2),
  boxShadow: theme.shadows[4],
  textAlign: 'center',
}));

const ErrorIconStyled = styled(ErrorIcon)(({ theme }) => ({
  fontSize: 80,
  color: theme.palette.error.main,
  marginBottom: theme.spacing(2),
}));

export const PaymentFailurePage: React.FC = () => {
  const { t } = useTranslation();
  const { currentLang } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [paymentId, setPaymentId] = useState<string | null>(null);

  useEffect(() => {
    // Get payment ID from URL parameters
    const paymentIdParam = searchParams.get('payment_id');
    setPaymentId(paymentIdParam);
  }, [searchParams]);

  const handleRetryPayment = () => {
    navigate('/checkout');
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <StyledPaper>
        <Box sx={{ direction: currentLang === 'he' ? 'rtl' : 'ltr' }}>
          <ErrorIconStyled />
          
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'error.main' }}>
            {t('payment.failure.title') || 'Payment Failed'}
          </Typography>
          
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {t('payment.failure.message') || 'Unfortunately, your payment could not be processed. This may be due to insufficient funds, an expired card, or a technical issue.'}
          </Typography>

          {paymentId && (
            <Box sx={{ mb: 3 }}>
              <Chip 
                label={`Payment ID: ${paymentId}`} 
                color="error" 
                variant="outlined"
                sx={{ fontSize: '0.875rem' }}
              />
            </Box>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            {t('payment.failure.details') || 'Please check your payment details and try again, or contact our support team if the problem persists.'}
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              variant="outlined"
              onClick={handleBackToHome}
              sx={{ borderRadius: 2 }}
            >
              {t('payment.failure.backToHome') || 'Back to Home'}
            </Button>
            <Button
              variant="contained"
              onClick={handleRetryPayment}
              sx={{ borderRadius: 2 }}
            >
              {t('payment.failure.tryAgain') || 'Try Again'}
            </Button>
          </Stack>
        </Box>
      </StyledPaper>
    </Container>
  );
}; 